import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import usePrevious from 'use-previous';
import { userIdentityShape, catalogBibShape, ugcVideoShape, shelfItemShape } from '@bibliocommons/bc-prop-types';
import { ExpandableText } from '@bibliocommons/expandable';
import { Movies } from '@bibliocommons/base-icons';
import UserBadge from '@bibliocommons/user-badge';
import Heading from '@bibliocommons/heading';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';
import PrivacyIndicator from 'app/components/bibs/shared/ugc/shared/PrivacyIndicator';
import { DeleteVideoModalContainer } from 'app/components/bibs/shared/ugc/DeleteVideoModal';
import VideoPlayback from 'app/components/shared/UGC/VideoPlayback/VideoPlayback';
import ContentWarnings from 'app/components/bibs/shared/ugc/shared/ContentWarnings';
import AuthorName from '../AuthorName';
import FlagUgcButtonContainer from '../FlagUgcButton';
import OptionsDropdown from '../OptionsDropdown';
import { VideoEditorContainer } from '../VideoEditor';
import useFlags from '../shared/useFlags';
import UserCardContainer from '../UserCard/UserCardContainer';

import './VideoDisplay.scss';

export default function VideoDisplay({
  author,
  catalogBib,
  displayStaffBadge,
  modalActions,
  handleDeleteSuccess,
  shelfItem,
  video
}) {
  const videoId = video.get('id');
  const { flags, contentIsHidden, placeholder, toggleContent } = useFlags(videoId);
  const [editorOpen, setEditorOpen] = useState(false);
  const prevEditorOpen = usePrevious(editorOpen);
  const optionsDropdownRef = useRef();

  useEffect(() => {
    // Set focus on the options dropdown when the editor is closed.
    if (!editorOpen && prevEditorOpen && optionsDropdownRef.current) {
      setFocusWithinElement(optionsDropdownRef.current);
    }
  }, [editorOpen, prevEditorOpen, optionsDropdownRef]);

  function handleDelete() {
    modalActions.openModal(`delete-video-${videoId}`);
  }

  function handleOpenEditor() {
    setEditorOpen(true);
  }

  function handleCloseEditor() {
    setEditorOpen(false);
  }

  function renderEditor() {
    return (
      <VideoEditorContainer
        existingVideo={video}
        metadataId={video.get('metadataId')}
        handleCancel={handleCloseEditor}
        handleSuccess={handleCloseEditor}
      />
    );
  }

  function renderDisplay() {
    return (
      <div className="video-display">
        <div className="video-wrapper">
          {contentIsHidden ? (
            <div className="placeholder-icon-wrapper">
              <Movies />
            </div>
          ) : (
            <VideoPlayback key={videoId} src={video.get('url')} title={video.get('title')} />
          )}
        </div>
        <div className="video-details">
          <div className="user-area">
            <UserCardContainer targetUser={author}>
              <AuthorName author={author} />
              <UserBadge userIdentity={author} displayStaffBadge={displayStaffBadge} ugcType="video" />
            </UserCardContainer>
          </div>
          <div className="right-side">
            <PrivacyIndicator author={author} shelfItem={shelfItem} />
            <OptionsDropdown
              id={videoId}
              ugcType="video"
              author={author}
              handleDelete={handleDelete}
              handleEdit={handleOpenEditor}
              ref={optionsDropdownRef}
            />
          </div>
          {contentIsHidden ? (
            placeholder
          ) : (
            <div className="video-info">
              <Heading size="modest" tagName="div" className="video-title">
                {video.get('title')}
              </Heading>
              {video.get('description') && (
                <ExpandableText
                  className="video-description"
                  text={video.get('description')}
                  truncateCount={240}
                  expandLinkHref={`#read-more-video-desc-${videoId}`}
                  textKey="video"
                />
              )}
            </div>
          )}
          {flags.get('flaggableTypes')?.size > 0 && <FlagUgcButtonContainer id={videoId} ugcType="video" />}
          <ContentWarnings
            author={author}
            flags={flags}
            contentIsHidden={contentIsHidden}
            toggleContent={toggleContent}
            ugcType="video"
          />
        </div>
        <DeleteVideoModalContainer videoId={videoId} catalogBib={catalogBib} handleSuccess={handleDeleteSuccess} />
      </div>
    );
  }

  return <div className="cp-video-display">{editorOpen ? renderEditor() : renderDisplay()}</div>;
}

VideoDisplay.defaultProps = {
  displayStaffBadge: true
};

VideoDisplay.propTypes = {
  author: userIdentityShape,
  catalogBib: catalogBibShape.isRequired,
  displayStaffBadge: PropTypes.bool,
  handleDeleteSuccess: PropTypes.func,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfItem: shelfItemShape,
  video: ugcVideoShape.isRequired
};
